import cn from "classnames";

import { BlueGradientDivider } from "@/components/blueGradientDivider/blueGradientDivider.component";
import { Text } from "@/components/text/text.component";

import {
  COMPARE_COMPETITOR_TABLE_APPEARANCES,
  COMPARE_COMPETITOR_TABLE_DEFAULTS,
  COMPARE_COMPETITOR_TABLE_SCROLL_MARGIN_TOP_PX,
} from "@/sanity/defaults/compareCompetitorTable.defaults";

import {
  columnHighlightClass,
  competitorColumnIndexes,
  resolveAppearance,
  usesMobileCompetitorSelect,
} from "./compareCompetitorTable.tableUtils";
import type { SanityCompareCompetitorTableQueryResult } from "./compareCompetitorTable.type";
import { CompareCompetitorTableColumnHeader } from "./compareCompetitorTableColumnHeader.component";
import {
  CompareCompetitorTableCtaRow,
  competitorTableCtaHref,
} from "./compareCompetitorTableCtaRow.component";
import { CompareCompetitorTableMobile } from "./compareCompetitorTableMobile.component";
import { CompareCompetitorTableRow } from "./compareCompetitorTableRow.component";

export const CompareCompetitorTableSection = ({
  block,
}: CompareCompetitorTableSectionProps) => {
  if (!block?.title) return null;

  const columns = block.columns ?? [];
  const rows = block.rows ?? [];

  if (!columns.length || !rows.length) return null;

  const footnotes = (block.footnotes ?? []).filter(Boolean);
  const sectionId =
    block.sectionId ?? COMPARE_COMPETITOR_TABLE_DEFAULTS.sectionId;
  const appearance = resolveAppearance(block.appearance);
  const highlightIndex = columns.findIndex((column) => column.isHighlighted);
  const competitorIndexes = competitorColumnIndexes(
    columns.length,
    highlightIndex,
  );
  const showMobileSelect = usesMobileCompetitorSelect(
    appearance,
    highlightIndex,
    competitorIndexes.length,
  );
  const columnCount = columns.length + 1;
  const hasCtaRow = Boolean(
    competitorTableCtaHref(appearance, block.ctaLink, highlightIndex),
  );

  return (
    <section
      id={sectionId}
      className="container my-10 w-full max-w-344 bg-white px-5 md:my-16 md:px-9"
      style={{ scrollMarginTop: COMPARE_COMPETITOR_TABLE_SCROLL_MARGIN_TOP_PX }}
      data-testid="compare-competitor-table-section">
      <Text
        variant="h3"
        component="h2"
        className="text-greyscale-headline mb-10 text-center md:mb-14">
        {block.title}
      </Text>

      {showMobileSelect ? (
        <div className="lg:hidden">
          <CompareCompetitorTableMobile
            title={block.title}
            columns={columns}
            rows={rows}
            highlightIndex={highlightIndex}
            appearance={appearance}
            ctaLabel={block.ctaLabel}
            ctaLink={block.ctaLink}
          />
        </div>
      ) : null}

      <div
        className={cn(
          "scrollbar-hide w-full min-w-0 overflow-x-auto",
          showMobileSelect && "hidden lg:block",
        )}>
        <table className="w-full table-fixed border-collapse">
          <caption className="sr-only">{block.title}</caption>
          <colgroup>
            <col className="w-[38%] lg:w-93" />
            {columns.map((column) => (
              <col key={column.id} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th scope="col" className="p-0 pb-5 align-bottom font-normal">
                <span className="sr-only">Feature</span>
              </th>
              {columns.map((column, columnIndex) => (
                <th
                  key={column.id}
                  scope="col"
                  className={cn(
                    "px-2 pt-6 pb-5 align-bottom font-normal",
                    columnHighlightClass(
                      columnIndex,
                      highlightIndex,
                      "header",
                      appearance,
                    ),
                  )}
                  data-testid="compare-competitor-table-column-header">
                  <CompareCompetitorTableColumnHeader
                    column={column}
                    columnIndex={columnIndex}
                    highlightIndex={highlightIndex}
                    appearance={appearance}
                  />
                </th>
              ))}
            </tr>
            <tr aria-hidden="true">
              <td colSpan={columnCount} className="p-0">
                {appearance === COMPARE_COMPETITOR_TABLE_APPEARANCES.adt ? (
                  <div className="border-greyscale-strength/20 h-px border-t" />
                ) : (
                  <BlueGradientDivider className="h-px!" />
                )}
              </td>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <CompareCompetitorTableRow
                key={row.id}
                row={row}
                columns={columns}
                rowIndex={rowIndex}
                rowCount={rows.length}
                highlightIndex={highlightIndex}
                appearance={appearance}
                hasCtaRow={hasCtaRow}
              />
            ))}
            <CompareCompetitorTableCtaRow
              columns={columns}
              highlightIndex={highlightIndex}
              appearance={appearance}
              ctaLabel={block.ctaLabel}
              ctaLink={block.ctaLink}
            />
          </tbody>
        </table>
      </div>

      {footnotes.length ? (
        <ul
          className="mt-8 list-none space-y-1"
          data-testid="compare-competitor-table-footnotes">
          {footnotes.map((footnote, index) => (
            <li key={`footnote-${index}`}>
              <Text variant="legal" component="p">
                {footnote}
              </Text>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

type CompareCompetitorTableSectionProps = {
  block: SanityCompareCompetitorTableQueryResult;
};
